<script setup lang="ts">
import { map, sumBy } from "lodash-es";
import moment from "moment-timezone";
import { useFormat } from "~/composables/use-format";

const { t } = useI18n();
const { c, n, d } = useFormat();
const { $api } = useNuxtApp();
const toast = useToast();
const { set: setTitle } = usePageTitle();

interface RevenueAnalytics {
  series: { id: string; label: string; revenue: number; orders: number }[];
}
interface SubscriptionSeriesRecord {
  id: string;
  label: string;
  signups: number;
  newCustomers: number;
  churnedCustomers: number;
  churnedMRR: number;
  mrr: number;
}
interface SubscriptionAnalytics {
  overview: { mrr: number; activeCustomers: number };
  series: SubscriptionSeriesRecord[];
}
interface UsageAnalytics {
  overview: { activeWorkspaces: number; activeUsers: number; totalWorkspaces: number };
  series: {
    id: string;
    label: string;
    orders: number;
    events: number;
    sites: number;
    products: number;
    emails: number;
    aiSessions: number;
  }[];
}

interface RecentWorkspace {
  id: string;
  name: string;
  createTime: number;
  plan?: { name: string };
  subscription?: { status: string };
}

const timezone = "Asia/Ho_Chi_Minh";
const firstLoading = ref(true);
// firstLoading stays true if the fetch throws, which would leave the spinners
// turning forever; this one is cleared either way.
const loading = ref(false);
const revenue = ref<RevenueAnalytics | null>(null);
// Monthly buckets back the MRR month-over-month delta; the growth chart runs
// on its own weekly call, so the two granularities never collide.
const subsMonthly = ref<SubscriptionAnalytics | null>(null);
const subsWeekly = ref<SubscriptionAnalytics | null>(null);
const subsRecent = ref<SubscriptionAnalytics | null>(null);
const subsPrevious = ref<SubscriptionAnalytics | null>(null);
const usage = ref<UsageAnalytics | null>(null);
const recentWorkspaces = ref<RecentWorkspace[]>([]);

function initials(name: string) {
  return name
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

const subscriptionStatuses: Record<string, { intent: string; key: string }> = {
  active: { intent: "positive", key: "statusActive" },
  trialing: { intent: "informative", key: "statusTrialing" },
  "past-due": { intent: "warning", key: "statusPastDue" },
  cancelled: { intent: "negative", key: "statusCancelled" },
  free: { intent: "neutral", key: "statusFree" },
};
function subStatus(status?: string) {
  return subscriptionStatuses[status ?? "free"] ?? subscriptionStatuses.free!;
}

const currentMonthKey = moment.tz(timezone).format("YYYY-MM");
const previousMonthKey = moment.tz(timezone).subtract(1, "month").format("YYYY-MM");
// Rolling 30 days = today + the 29 days before it.
const last30Boundary = moment.tz(timezone).subtract(29, "days").startOf("day").format("YYYY-MM-DD");

const currentSubMonth = computed(
  () => subsMonthly.value?.series.find((m) => m.id === currentMonthKey) ?? null,
);
const previousSubMonth = computed(
  () => subsMonthly.value?.series.find((m) => m.id === previousMonthKey) ?? null,
);

// The revenue fetch spans 60 days: the last 30 feed the chart and headline,
// the 30 before are the comparison base for the delta chip.
const revenueCurrent = computed(() =>
  (revenue.value?.series ?? []).filter((m) => m.id >= last30Boundary),
);
const revenueRecent = computed(() => sumBy(revenueCurrent.value, "revenue"));
const revenuePrevious = computed(() =>
  sumBy((revenue.value?.series ?? []).filter((m) => m.id < last30Boundary), "revenue"),
);

// Customer movement over the window: two scoped subscription calls, summed
// across their (monthly) buckets so a window crossing a month still counts.
const recentNew = computed(() => sumBy(subsRecent.value?.series ?? [], "newCustomers"));
const recentSignups = computed(() => sumBy(subsRecent.value?.series ?? [], "signups"));
const recentChurned = computed(() => sumBy(subsRecent.value?.series ?? [], "churnedCustomers"));
const recentChurnedMRR = computed(() => sumBy(subsRecent.value?.series ?? [], "churnedMRR"));
const previousNew = computed(() => sumBy(subsPrevious.value?.series ?? [], "newCustomers"));
const previousChurned = computed(() => sumBy(subsPrevious.value?.series ?? [], "churnedCustomers"));

function percentDelta(current: number, previous: number) {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}
const revenueDelta = computed(() => percentDelta(revenueRecent.value, revenuePrevious.value));
const newCustomersDelta = computed(() => percentDelta(recentNew.value, previousNew.value));
const mrrDelta = computed(() =>
  percentDelta(currentSubMonth.value?.mrr ?? 0, previousSubMonth.value?.mrr ?? 0),
);
const churnedDelta = computed(() => recentChurned.value - previousChurned.value);

const revenueChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  xAxis: { type: "category", data: map(revenueCurrent.value, (m) => m.id.slice(5)) },
  yAxis: { type: "value", splitLine: { lineStyle: { type: "dashed" } } },
  series: [
    {
      name: t("analytics.chart.revenue"),
      type: "line",
      smooth: true,
      showSymbol: false,
      areaStyle: { opacity: 0.12 },
      data: map(revenueCurrent.value, "revenue"),
    },
  ],
}));

const customersChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: { data: [t("analytics.chart.signups"), t("analytics.chart.newPaying")] },
  xAxis: { type: "category", data: map(subsWeekly.value?.series ?? [], "label") },
  yAxis: { type: "value", minInterval: 1, splitLine: { lineStyle: { type: "dashed" } } },
  series: [
    {
      name: t("analytics.chart.signups"),
      type: "bar",
      barMaxWidth: 16,
      itemStyle: { borderRadius: [4, 4, 0, 0] },
      data: map(subsWeekly.value?.series ?? [], "signups"),
    },
    {
      name: t("analytics.chart.newPaying"),
      type: "bar",
      barMaxWidth: 16,
      itemStyle: { borderRadius: [4, 4, 0, 0], opacity: 0.55 },
      data: map(subsWeekly.value?.series ?? [], "newCustomers"),
    },
  ],
}));

const actionKeys = [
  { key: "orders", field: "orders" },
  { key: "events", field: "events" },
  { key: "sites", field: "sites" },
  { key: "products", field: "products" },
  { key: "emails", field: "emails" },
  { key: "ai", field: "aiSessions" },
] as const;

const usageChartOption = computed(() => ({
  tooltip: { trigger: "axis" },
  legend: { data: actionKeys.map((a) => t(`analytics.action_${a.key}`)) },
  xAxis: { type: "category", data: map(usage.value?.series ?? [], "label") },
  yAxis: { type: "value", minInterval: 1 },
  series: actionKeys.map((a) => ({
    name: t(`analytics.action_${a.key}`),
    type: "line",
    smooth: true,
    data: map(usage.value?.series ?? [], a.field),
  })),
}));

async function load() {
  const now = moment.tz(timezone);
  // Weekly buckets aligned to ISO week starts, so only the current week is partial.
  const weeklyStart = now.clone().subtract(4, "weeks").startOf("isoWeek").unix();
  const monthlyStart = now.clone().subtract(1, "month").startOf("month").unix();
  const windowStart = now.clone().subtract(29, "days").startOf("day").unix();
  const prevWindowStart = now.clone().subtract(59, "days").startOf("day").unix();
  const prevWindowEnd = now.clone().subtract(30, "days").endOf("day").unix();
  loading.value = true;
  try {
    const [revenueData, monthlyData, weeklyData, subsRecentData, subsPreviousData, usageData, workspaceData] =
      await Promise.all([
        $api<RevenueAnalytics>("/analytics/revenue", {
          query: { groupBy: "day", from: prevWindowStart },
        }),
        $api<SubscriptionAnalytics>("/analytics/subscriptions", { query: { from: monthlyStart } }),
        $api<SubscriptionAnalytics>("/analytics/subscriptions", {
          query: { from: weeklyStart, groupBy: "week" },
        }),
        $api<SubscriptionAnalytics>("/analytics/subscriptions", { query: { from: windowStart } }),
        $api<SubscriptionAnalytics>("/analytics/subscriptions", {
          query: { from: prevWindowStart, to: prevWindowEnd },
        }),
        $api<UsageAnalytics>("/analytics/usage", { query: { from: weeklyStart, groupBy: "week" } }),
        $api<{ items: RecentWorkspace[] }>("/workspaces", { query: { pageSize: 5 } }),
      ]);
    revenue.value = revenueData;
    subsMonthly.value = monthlyData;
    subsWeekly.value = weeklyData;
    subsRecent.value = subsRecentData;
    subsPrevious.value = subsPreviousData;
    usage.value = usageData;
    recentWorkspaces.value = workspaceData.items;
    firstLoading.value = false;
  } catch {
    toast.error({ title: t("message.loadFailed") });
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  setTitle(t("navigation.dashboard"));
  await load();
});
</script>

<template>
  <div class="flex flex-col gap-large">
    <div class="grid grid-cols-2 gap-medium md:grid-cols-4">
      <MetricCard
        size="large"
        :loading="firstLoading"
        :label="t('analytics.kpi.revenue')"
        :hint="t('analytics.hint.revenue')"
        :value="c(revenueRecent, 'USD')"
        :delta="revenueDelta"
        :delta-label="t('analytics.kpi.vsPrevPeriod')"
      />
      <MetricCard
        size="large"
        :loading="firstLoading"
        :label="t('analytics.kpi.newCustomers')"
        :hint="t('analytics.hint.newCustomers')"
        :value="n(recentNew, 'integer')"
        :secondary="`/ ${n(recentSignups, 'integer')} ${t('analytics.chart.signups').toLowerCase()}`"
        :delta="newCustomersDelta"
        :delta-label="t('analytics.kpi.vsPrevPeriod')"
      />
      <MetricCard
        size="large"
        :loading="firstLoading"
        :label="t('analytics.kpi.mrr')"
        :hint="t('analytics.hint.mrr')"
        :value="c(subsMonthly?.overview.mrr ?? 0, 'USD')"
        :delta="mrrDelta"
        :delta-label="t('analytics.kpi.vsLastMonth')"
      />
      <MetricCard
        size="large"
        :loading="firstLoading"
        :label="t('analytics.kpi.churned')"
        :hint="t('analytics.hint.churned')"
        :value="n(recentChurned, 'integer')"
        :secondary="recentChurnedMRR ? `/ -${c(recentChurnedMRR, 'USD')} ${t('analytics.kpi.mrr')}` : ''"
        :delta="churnedDelta"
        delta-format="integer"
        :delta-label="t('analytics.kpi.vsPrevPeriod')"
        delta-inverted
      />
    </div>

    <div class="grid grid-cols-1 gap-medium lg:grid-cols-3">
      <SectionCard
        :title="t('analytics.chart.revenue')"
        :description="t('dateRange.last30Days')"
        class="lg:col-span-2"
      >
        <Chart :option="revenueChartOption" :loading="loading" :height="320" />
      </SectionCard>
      <SectionCard :title="t('analytics.newestWorkspacesTitle')">
        <template #actions>
          <NuxtLink to="/workspace">
            <Button variant="text-neutral" size="small">{{ t("analytics.viewAll") }} →</Button>
          </NuxtLink>
        </template>

        <!-- Sits beside a chart that spins while it loads; an empty card next
             to that reads as broken rather than pending. -->
        <div v-if="loading" class="recent recent__loading">
          <ProgressCircle size="medium" />
        </div>

        <div v-else class="recent">
          <NuxtLink
            v-if="recentWorkspaces[0]"
            :to="`/workspace/${recentWorkspaces[0].id}`"
            class="recent__item is-featured"
          >
            <Avatar :initials="initials(recentWorkspaces[0].name)" size="large" shape="square" />
            <div class="flex min-w-0 flex-col">
              <span class="recent__name h4-primary truncate">{{ recentWorkspaces[0].name }}</span>
              <span class="text-body-small-tertiary content-neutral-medium truncate">
                {{ recentWorkspaces[0].plan?.name ?? "—" }} · {{ d(recentWorkspaces[0].createTime, "short") }}
              </span>
            </div>
          </NuxtLink>
          <NuxtLink
            v-for="workspace in recentWorkspaces.slice(1)"
            :key="workspace.id"
            :to="`/workspace/${workspace.id}`"
            class="recent__item"
          >
            <div class="flex min-w-0 items-center gap-xsmall">
              <Avatar :initials="initials(workspace.name)" size="small" shape="square" />
              <div class="flex min-w-0 flex-col">
                <span class="recent__name text-body-secondary truncate">{{ workspace.name }}</span>
                <span class="text-body-small-tertiary content-neutral-medium truncate">
                  {{ workspace.plan?.name ?? "—" }} · {{ d(workspace.createTime, "short") }}
                </span>
              </div>
            </div>
            <Badge :intent="(subStatus(workspace.subscription?.status).intent as any)" emphasis="medium" size="small">
              {{ t(`workspace.subscription.${subStatus(workspace.subscription?.status).key}`) }}
            </Badge>
          </NuxtLink>
        </div>
      </SectionCard>
    </div>

    <div class="grid grid-cols-1 gap-medium lg:grid-cols-2">
      <SectionCard :title="t('analytics.chart.customerGrowth')" :description="t('dateRange.last30Days')">
        <Chart :option="customersChartOption" :loading="loading" />
      </SectionCard>
      <SectionCard :title="t('analytics.usageTitle')" :description="t('dateRange.last30Days')">
        <Chart :option="usageChartOption" :loading="loading" />
      </SectionCard>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Holds the featured item and the rows together — as separate SectionCard
// children its gap opened a hole under the featured item's divider. It also
// absorbs the leftover height so this card matches the chart card beside it.
.recent {
  display: flex;
  flex: 1;
  flex-direction: column;

  &__loading {
    align-items: center;
    justify-content: center;
  }

  // A NuxtLink, so it needs the colour and underline reset.
  &__item {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: space-between;
    gap: token("gap-xsmall");
    min-height: token("dm-48");
    padding: token("padding-xsmall") 0;
    color: inherit;
    text-decoration: none;
    border-bottom: 1px solid token("color-border-neutral-alpha-light-default");

    &:last-child {
      border-bottom: none;
    }

    &:hover .recent__name {
      color: token("color-content-primary");
    }

    // Sits above the list rather than sharing their even split, and holds
    // only an avatar plus its text so it stays left-aligned.
    &.is-featured {
      flex: 0 0 auto;
      justify-content: flex-start;
      gap: token("gap-small");
      padding-top: 0;
      padding-bottom: token("padding-small");
    }
  }

  &__name {
    transition: color token("motion-duration-fast") token("motion-ease-standard");
  }
}
</style>
